<template>
  <WdsStack justify-space-between class="tw-mb-7">
    <WdsHeading as="h1" type="display">
      {{ $t('API Tokens') }}
    </WdsHeading>

    <WdsStack align-center gap-sm>
      <WdsButton primary data-test="api-token-management-create-button" @click="createModal.open">
        {{ $t('Create token') }}
      </WdsButton>
    </WdsStack>
  </WdsStack>

  <WdsStack>
    <WdsText>
      {{
        $t(
          'Create and manage API tokens to authenticate API requests. These tokens are long-lived and will automatically expire after 6 months.',
        )
      }}
    </WdsText>
  </WdsStack>

  <WdsAlert
    v-if="createdAccessToken"
    :title="$t('Created access token')"
    class="tw-my-4"
    closable
    @close="createdAccessToken = ''"
  >
    <template #description>
      <WdsText class="tw-mb-4">
        {{ $t('Please note, the access token will only be shown once.') }}
      </WdsText>
      <WdsInput
        :model-value="createdAccessToken"
        :label="$t('Access token')"
        readonly
        @click="(event) => (event.target as HTMLInputElement).select()"
      />
    </template>
  </WdsAlert>

  <WdsAlert
    v-if="revokedTokenName"
    :title="$t('Revoked access token')"
    class="tw-my-4"
    closable
    @close="revokedTokenName = ''"
  >
    <template #description>
      <WdsText class="tw-mb-4">
        {{ $t('Successfully revoked token ":name".', { name: revokedTokenName }) }}
      </WdsText>
    </template>
  </WdsAlert>

  <div class="tw-overflow-x-auto tw-mt-8">
    <div class="tw-inline-block tw-min-w-full tw-py-2 tw-align-middle">
      <div class="tw-border tw-border-gray-200 tw-rounded">
        <table class="tw-min-w-full tw-divide-y tw-divide-gray-300">
          <thead class="tw-bg-gray-50">
            <tr>
              <th
                scope="col"
                class="tw-py-3.5 tw-pl-4 tw-pr-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600 sm:tw-pl-6"
              >
                {{ $t('Name') }}
              </th>
              <th
                scope="col"
                class="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600"
              >
                {{ $t('Scopes') }}
              </th>
              <th
                scope="col"
                class="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600"
              >
                {{ $t('Expiry') }}
              </th>
              <th scope="col" class="tw-relative tw-py-3.5 tw-pl-3 tw-pr-4 sm:tw-pr-6">
                <span class="tw-sr-only">{{ $t('Actions') }}</span>
              </th>
            </tr>
          </thead>
          <tbody v-if="tokens.length > 0" class="tw-divide-y tw-divide-gray-200 tw-bg-white">
            <tr v-for="token in tokens" :key="token.id">
              <td
                class="tw-whitespace-nowrap tw-py-4 tw-pl-4 tw-pr-3 tw-text-sm tw-font-medium tw-text-gray-900 sm:tw-pl-6"
              >
                {{ token.name }}
              </td>
              <td
                class="tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500 tw-font-mono"
              >
                {{ token.scopes.join(', ') }}
              </td>
              <td
                class="tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500 tw-font-mono"
              >
                {{ useDate(token.expires_at).date }}
              </td>
              <td
                class="tw-relative tw-whitespace-nowrap tw-py-4 tw-pl-3 tw-pr-4 tw-text-right tw-text-sm tw-font-medium sm:tw-pr-6"
              >
                <RevokeTokenButton :token="token" @revoked="handleTokenRevoked" />
              </td>
            </tr>
          </tbody>
          <tbody v-else class="tw-divide-y tw-divide-gray-200 tw-bg-white">
            <tr class="tw-text-center">
              <td class="tw-py-2" colspan="4">
                <div class="tw-justify-center">{{ $t('No API access tokens were found.') }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <WdsModal
    :title="$t('Create API token')"
    :show="createModal.isVisible.value"
    data-test="api-token-management-create-modal"
    @cancel="createModal.close"
  >
    <BaseHtmlForm
      action="/tokens"
      method="POST"
      novalidate
      @form-submitted.prevent="handleCreateToken"
    >
      <slot name="inputs-hidden" />

      <div>
        <WdsInput
          v-model="formState.name"
          :error="getFieldError('name')"
          :label="$t('Token name')"
          lg
          name="name"
          :placeholder="$t('Enter a name for your API token')"
          autocomplete="off"
          required
          type="text"
          data-test="api-token-management-name-input"
          @blur="nameTouch"
        />

        <WdsButton
          class="!tw-mt-[24px] tw-no-spinner"
          :disabled="error"
          full-width
          lg
          primary
          type="submit"
          data-test="api-token-management-submit"
          @click="touchAll"
        >
          {{ $t('Create token') }}
        </WdsButton>
      </div>
    </BaseHtmlForm>
  </WdsModal>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsHeading,
  WdsInput,
  WdsModal,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { helpers, required } from '@vuelidate/validators'
import { useOAuthTokens, useModal } from './composables'
import type { ValidationErrors } from './types'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import RevokeTokenButton from '@/components/api/tokens/RevokeTokenButton.vue'
import { $t } from '@/composables/translation'
import useDate from '@/composables/useDate'
import useCustomVuelidate from '@/composables/useCustomVuelidate'

interface Props {
  errors: ValidationErrors
}

const props = defineProps<Props>()

// API composable
const { tokens, fetchTokens, createToken } = useOAuthTokens()

// Modal state
const createModal = useModal()

// Form state
const formState = reactive({
  name: '',
})

// Created token display
const createdAccessToken = ref('')
const revokedTokenName = ref('')

// Validation with useCustomVuelidate
const validationRules = {
  name: {
    required: helpers.withMessage($t('Please enter a token name'), required),
  },
}

const {
  name: nameError,
  nameTouch,
  touchAll,
  resetAll,
  error,
} = useCustomVuelidate(validationRules, formState)

// Combine validation errors with backend errors
function getFieldError(field: 'name'): string | undefined {
  // Check backend errors first
  if (Object.prototype.hasOwnProperty.call(props.errors, field)) {
    return props.errors[field][0]
  }

  // Then check validation errors
  return nameError.value
}

// Handlers
async function handleCreateToken(): Promise<void> {
  touchAll()
  if (error.value) return

  const response = await createToken(formState.name)

  // Store access token for display
  createdAccessToken.value = response.accessToken

  // Reset form and close modal
  formState.name = ''
  resetAll()
  createModal.close()
}

function handleTokenRevoked(tokenName: string): void {
  revokedTokenName.value = tokenName
  fetchTokens()
}

// Lifecycle
onMounted(() => {
  fetchTokens()
})
</script>
